import _ from 'lodash';
import {
  CarrierResource,
  CountryResource,
  LocalizationResource,
} from '../services/resource/ResourceInterfaces';
import ResourceService from '../services/resource/ResourceService';
import moment from 'moment';
// Please avoid using redux stuff in here as it make unit test not work (because of circular dependencies)

const URLToObject = (url: any) => {
  const parsedUrl = decodeURIComponent(url);
  const request: any = {};

  const pairs = parsedUrl.substring(parsedUrl.indexOf('?') + 1).split('&');

  if (pairs.length > 0 && pairs[0] !== '') {
    for (let i = 0; i < pairs.length; i++) {
      let pair = pairs[i].split('=');
      request[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
    }
  }

  return request;
};

const checkIfRunningInContainer = (): { appId: string; kiosk: string; platform: string } => {
  // parse the URL and set initial state values
  let urlParams = URLToObject(window.location.search);
  let appId = '';
  let kiosk = '';
  let platform = '';

  if (urlParams.appid) {
    appId = urlParams.appid;
  }

  if (urlParams.kiosk === 'true') {
    kiosk = urlParams.kiosk;
  }

  if (urlParams.platform) {
    platform = urlParams.platform;
  }

  return { appId, kiosk, platform };
};

/** Customizer for lodash mergeWith(), avoid overwrite attributes on
 * `objValue` with same name attributes from `srcValue`.
 *
 * More practical example:
 *
 * `model = _.mergeWith(model, initModel, noOverwriteCustomizer)`
 *
 * This will guanratee all attributes in `model` will not be overwrote, while
 * also copying non-duplicated attributes from `initModel` to `model`.
 */
const noOverwriteCustomizer = (objValue: any, srcValue: any) => {
  if (objValue !== undefined && objValue !== null) {
    return objValue; // keep existing value
  }
  return srcValue; // only set if missing
};

/**
 * Create query string for request with a object.
 *
 * Example: /api/get-something?subtype=email&type=single_mbp
 * @param data JSON object
 * @returns queryString
 */
function createQueryString(data: any) {
  let queryString = Object.keys(data)
    .map(function (key) {
      return key + '=' + data[key];
    })
    .join('&');
  return queryString;
}

function getFormattedFqtvPrograms(fqtvPrograms: any[]) {
  let formattedFqtvPrograms = {};

  fqtvPrograms.forEach((fqtvProgram: any) => {
    let obj: any = {};
    obj[fqtvProgram.code] = fqtvProgram.name;
    formattedFqtvPrograms = _.extend(formattedFqtvPrograms, obj);
  });

  if (fqtvPrograms) {
    return formattedFqtvPrograms;
  } else {
    return {};
  }
}

export type MessageStatus = 'error' | 'warning' | 'success';

function getMessageTypes(messages: any) {
  let messageKeys = _.keys(messages);
  let types: MessageStatus[] = [];
  //   {
  //     "error": {
  //         "addpax_all_checked_in": "CKI_ADDPAX_ALL_PAX_CHECKED_IN"
  //     },
  //     "warning": {
  //         "any_any_any_any_croatia_any_any_ck_selectflight":
  //         "ANY_ANY_ANY_ANY_Croatia_ANY_ANY_CK_SELECTFLIGHT"
  //     }
  //   }
  if (_.includes(messageKeys, 'error')) {
    types.push('error');
  }

  if (_.includes(messageKeys, 'warning')) {
    types.push('warning');
  } else if (_.includes(messageKeys, 'success')) {
    types.push('success');
  }

  if (types.length === 0) {
    // Default value
    types.push('error');
  }
  return types;
}

function getCurrentPage() {
  let url = window.location.pathname;
  const match = url.match(/ck_(.*)/);
  let res = match ? match[0] : '';
  return res;
}

function getCurrentPageAlertName() {
  let url = window.location.pathname;
  const match = url.match(/ck_(.*)/);
  let res = match ? match[0] : '';
  res = res.replace(/^ck_/, '');
  let finalRes = res;
  switch (res) {
    case 'selectflight':
      finalRes = 'selectFlightError';
      break;
    default:
      finalRes = finalRes + 'Error';
      break;
  }
  return finalRes;
}

/**
 * Get all states of a country.
 * @param countryCode
 * @returns
 */
function getStateOfCountry(countryCode: string, countries: CountryResource[]) {
  if (_.isUndefined(countryCode) || _.isEmpty(countryCode)) {
    return [];
  }

  let country = _.find(countries, { code: countryCode });
  if (_.isUndefined(country)) {
    return [];
  }
  return country.states;
}

function objToArrSelectBoxCollection(objectCollection: any) {
  let arrayCollection = [];
  for (let value in objectCollection) {
    // http://stackoverflow.com/questions/1963102/what-does-the-jslint-error-body-of-a-for-in-should-be-wrapped-in-an-if-statemen
    if (objectCollection.hasOwnProperty(value)) {
      let label = objectCollection[value];

      arrayCollection.push({
        value: value,
        name: label,
      });
    }
  }
  return arrayCollection;
}

function buildHometagsSelectbox(start: any, limit: any) {
  let options: any = {};
  _.times(limit - start, function (index: any) {
    let option = parseInt(start, 10) + parseInt(index, 10);
    options[option.toString()] = option;
  });
  return options;
}

function areWeInTestMode(): boolean {
  return import.meta.env.JEST_WORKER_ID !== undefined;
}

const getInvalidFieldErrorResponse = (response: any, localizations: LocalizationResource) => {
  let key = Object.keys(response.data.invalidFields)[0];
  let errorCode = response.data.invalidFields[key];

  // If error response is deeply nested, keep drilling to get the first one
  while (!_.isString(errorCode) && !_.isUndefined(errorCode)) {
    let errorObject = errorCode;
    key = Object.keys(errorObject)[0];
    errorCode = errorObject[key];
  }

  if (_.isUndefined(errorCode)) {
    return '';
  }

  let errorMessage: string = localizations['error'][errorCode];
  if (_.isUndefined(errorMessage)) {
    errorMessage = errorCode;
  }
  return errorMessage;
};

const getOperatingSystem = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (
    userAgent.indexOf('iphone') > -1 ||
    userAgent.indexOf('ipad') > -1 ||
    userAgent.indexOf('ipod') > -1
  ) {
    return 'ios';
  }
  if (
    userAgent.indexOf('macintosh') > -1 &&
    userAgent.indexOf('safari') > -1 &&
    userAgent.indexOf('chrome') === -1
  ) {
    return 'ios'; // It's actually 'macos' but function-wise they are same as ios anyway
  }
  // If not iOS or macOS, then return android (even if the user can be windows or other os)
  return 'android';
};

const DEEPLINK_URL_MAPPINGS = {
  locale: 'langCode',
  first_name1: 'firstName',
  last_name1: 'lastName',
  departure_port: 'departureAirport',
  departure_date: 'dayOfDeparture',
  flight_number: function (urlParams: any) {
    let carrier = urlParams.carrier || '';
    let output = ['flightNumber', carrier + urlParams.flight_number];
    return output;
  },
  paxflight: 'retrievalData',
  etkt: 'etix',
};

const DEEPLINK_CONDITIONS = ['lastName', 'retrievalData'];

/**
 * Normalize the key name in URL data using a map.
 * @param urlData
 * @param urlMappings
 * @returns
 */
const normalizeURLData = function (urlData: any, urlMappings: any) {
  if (_.isEmpty(urlMappings)) {
    return urlData;
  }

  let normalizedURL = _.reduce(
    urlData,
    function (result: any, value: string, key: string, urlData: any) {
      let mapKey = '';

      if (urlMappings[key]) {
        // Currently, only flight_number in mapping is a function
        if (_.isFunction(urlMappings[key])) {
          // Run the function with [urlData] as arguments
          mapKey = urlMappings[key](...[urlData]);
          result[mapKey[0]] = mapKey[1];
        } else {
          mapKey = urlMappings[key];
          result[mapKey] = value;
        }
      } else {
        result[key] = value;
      }

      return result;
    },
    {}
  );

  return normalizedURL;
};

const parseURL = (queryString: string): any => {
  // parse the URL and set initial state values
  let urlParams = URLToObject(queryString);
  let isDeeplink = false;
  if (_.isEmpty(urlParams)) {
    // Nothing to parse
    return { normalizedURLParams: {}, isDeeplink };
  }
  // Parse different types of URL to eeStructure
  let normalizedURLParams = normalizeURLData(urlParams, DEEPLINK_URL_MAPPINGS);

  if (!_.isEmpty(normalizedURLParams)) {
    let keys = _.keys(normalizedURLParams);
    // Check for deeplink condition
    // extend the check for deeplink multiple conditions
    _.each(DEEPLINK_CONDITIONS, function (deeplinkCondition) {
      if (_.includes(keys, deeplinkCondition)) {
        isDeeplink = true;
      }
    });
  }
  return { normalizedURLParams, isDeeplink };
};

const openInNewTab = (url: string): void => {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) newWindow.opener = null;
};

/**
 * Detect the user platform based on user Agent (so parameter such as appId and platform won't matter).
 * Return true if user is Desktop.
 */
const trueDetectDesktop = (): boolean => {
  const userAgent = navigator.userAgent;
  const isMobile = /Mobi|Android|iPhone|iPad|iPod|Windows Phone|BlackBerry/i.test(userAgent);
  return !isMobile;
};

function calculateDevice(appId: string, kiosk: string): string {
  const mobileWidthThreshold: number = 768;
  let device: string = 'DESKTOP';
  if (appId) {
    device = 'MOBILE_APP';
  } else if (kiosk) {
    device = 'SSK';
  } else if (window.screen.width <= mobileWidthThreshold) {
    device = 'MOBILE';
  }

  return device;
}

const clearQueryDataFromURL = () => {
  window.history.pushState({}, document.title, document.location.pathname + document.location.hash);
};

const scrollToElement = (currentScroll: number, offset: number) => {
  const targetPosition = Math.max(0, currentScroll + offset);

  window.scrollTo({
    top: targetPosition,
    behavior: 'smooth',
  });
};

const isElementInViewport = (el: HTMLElement | null): boolean => {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= window.innerHeight &&
    rect.right <= window.innerWidth
  );
};

const scrollToSeat = (seatNumber: string) => {
  const seatElement = document.querySelector(
    `.js-seatmap-seat[value="${seatNumber}"]`
  ) as HTMLElement | null;

  if (seatElement) {
    seatElement.scrollIntoView({ block: 'center' });

    seatElement.focus();
  }
};

// TODO find a better place for this
const getMappedMatchboxLocalization = (localizations: LocalizationResource) => {
  let screentext = localizations.ck_updateidentitydocument;
  if (!localizations.ck_updateidentitydocument) {
    return null;
  }
  let mappedText = {
    compliancePass: screentext.compliancepass,
    na: screentext.na,
    change: screentext.change,
    error: screentext.error,
    error_oops: screentext.error_oops,
    not_found: screentext.not_found,
    select: screentext.select,
    Standard: {
      validation: screentext.validation,
      continue: screentext.continue,
      skip: screentext.skip,
      yes: screentext.yes,
      no: screentext.no,
      ok: screentext.ok,
      cancel_btn: screentext.cancel_btn,
      continue_btn: screentext.continue_btn,
      submit: screentext.submit,
      id: screentext.id,
      doe: screentext.doe,
      na: screentext.na,
      required: screentext.required,
      invalidDate: screentext.invaliddate,
      skip_btn: screentext.skip_btn,
      check_document_description: screentext.check_document_description,
      check_form_data: screentext.check_form_data,
      check_files_data: screentext.check_files_data,
      delete_document_btn: screentext.delete_document_btn,
      delete_photo_btn: screentext.delete_photo_btn,
    },
    dropzone: {
      label: screentext.label,
      max_count: screentext.max_count,
      uploading: screentext.uploading,
    },
    Statuses: {
      failed: screentext.failed,
      succeeded: screentext.succeeded,
      loading: screentext.loading,
    },
    FlightData: {
      flight: screentext.flight,
      airport: screentext.airport,
      date: screentext.date,
    },
    ValidationResult: {
      title: screentext.validationresult_title,
      warning: screentext.validationresult_warning,
      succeeded: {
        title: screentext.validationresult_succeeded_title,
        subtitle: screentext.validationresult_succeeded_subtitle,
        description: screentext.validationresult_succeeded_description,
      },
      reasons: screentext.validationresult_reasons,
      failed: {
        title: screentext.validationresult_failed_title,
        list_title: screentext.validationresult_failed_list_title,
        list_upload_documents: screentext.validationresult_failed_list_upload_documents,
        list_checking_at_airport: screentext.validationresult_failed_list_checking_at_airport,
      },
      cancelled: {
        title: screentext.validationresult_cancelled_title,
        description: screentext.validationresult_cancelled_description,
      },
      unknown: screentext.validationresult_unknown,
      shortId: screentext.validationresult_shortid,
      finish_button: screentext.validationresult_finish_button,
      restart_button: screentext.validationresult_restart_button,
      start_button: screentext.validationresult_start_button,
    },
    QRUploader: {
      cancel_btn: screentext.cancel_btn,
      reload_btn: screentext.reload_btn,
    },
    Uploader: {
      or: screentext.or,
      show_qr_modal_btn: screentext.show_qr_modal_btn,
      delete_all_btn: screentext.delete_all_btn,
    },
    errors: {
      needFile: screentext.needfile,
      no_tickets: screentext.no_tickets,
      failed_to_load_details: screentext.failed_to_load_details,
      initSessionError: screentext.initsessionerror,
      cancelSessionError: screentext.cancelsessionerror,
      WRONG_CAPTCHA: screentext.wrong_captcha,
      BOOKING_NOT_FOUND: screentext.booking_not_found,
      DOCUMENT_ALREADY_UPLOADED: screentext.document_already_uploaded,
      INVALID_SCHEMA_STEP: screentext.invalid_schema_step,
      WRONG_PARAMS: screentext.wrong_params,
      FORM_NOT_FOUND: screentext.form_not_found,
      FLIGHT_TICKET_NOT_FOUND: screentext.flight_ticket_not_found,
      SCHEMA_NOT_FOUND: screentext.schema_not_found,
      SCHEMA_STEP_NOT_FOUND: screentext.schema_step_not_found,
      WRONG_SCHEMA_STEP: screentext.wrong_schema_step,
      SESSION_MAX_ATTEMPT: screentext.session_max_attempt,
      DIFFERENT_FLIGHT_TICKET_AND_PASSPORT_OWNER:
        screentext.different_flight_ticket_and_passport_owner,
      FLIGHT_TICKET_ALREADY_VALIDATED: screentext.flight_ticket_already_validated,
      FLIGHT_TICKET_ALREADY_BINDED: screentext.flight_ticket_already_binded,
      FLIGHT_DATE_EXPIRED: screentext.flight_date_expired,
      SESSION_ALREADY_VALIDATED: screentext.session_already_validated,
      MORE_PAGES_AVAILABLE: screentext.more_pages_available,
      DOCUMENT_UPLOAD_FAILED: screentext.document_upload_failed,
      INSUFFICIENT_PAGES_PROVIDED: screentext.insufficient_pages_provided,
      LIMIT_FILE_SIZE: screentext.limit_file_size,
      LIMIT_FILE_COUNT: screentext.limit_file_count,
      LIMIT_UNEXPECTED_FILE: screentext.limit_unexpected_file,
      service_is_temporarily_unavailable: screentext.service_is_temporarily_unavailable,
      gateway_timeout: screentext.gateway_timeout,
      wrong_token: screentext.wrong_token,
      execute_recaptcha_not_available: screentext.execute_recaptcha_not_available,
      warning_recaptcha: screentext.warning_recaptcha,
      maxUploadCount: screentext.maxuploadcount,
    },
    fields: {
      dateofbirth: screentext.dateofbirth,
      dateofexpiry: screentext.dateofexpiry,
      nationalitycode: screentext.nationalitycode,
      documentnumber: screentext.documentnumber,
      visatype: screentext.visatype,
      numberofentries: screentext.numberofentries,
      visasubclass: screentext.visasubclass,
      passenger: screentext.passenger,
      document_number: screentext.document_number,
      ticket_number: screentext.ticket_number,
    },
    values: {
      true: screentext.true,
      false: screentext.false,
    },
    reasons: {
      rejected_by_recognizer: screentext.rejected_by_recognizer,
      wrong_type: screentext.wrong_type,
      owner_not_recognized: screentext.owner_not_recognized,
      failed_to_set_owner: screentext.failed_to_set_owner,
      different_flight_ticket_and_passport_owner:
        screentext.different_flight_ticket_and_passport_owner,
      not_recognized: screentext.not_recognized,
    },
  };
  return mappedText;
};

const createComplexMessageObject = (params: any) => {
  const { key, textId, list } = params;

  return {
    [key]: {
      '0': textId,
      search: {
        searchFor: 'PAX',
        value: list.join(', '),
      },
    },
  };
};

const getPaxInitials = (firstName: string, lastName: string): string => {
  return firstName.charAt(0) + lastName.charAt(0);
};

const getOperatingAirlineFromCarrierCode = (carrierCode: string, carriers: CarrierResource[]) => {
  let found = '';
  carriers.forEach((carrier) => {
    if (carrier.carrierCode === carrierCode) {
      found = carrier.name;
      return;
    }
  });
  return found;
};

/**
 * Convert full uppercase name like JOHN DOE into John Doe
 * @param name
 * @returns
 */
const toProperCase = (name: string) => {
  return name
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const focusAndScrollIntoView = (element: any) => {
  element.scrollIntoView();
  element.focus();
};

const focusFirstInvalidInput = (element: HTMLElement) => {
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    element.focus();
  }
};

const getDayOfWeekFromDate = (date: string, lang: string = 'en', shorthand: boolean = false) => {
  if (date === '') {
    return '';
  }

  const localizations = ResourceService.getLocalizations();
  let day: string = localizations.daylong[moment(date, 'YYYY-MM-DD').locale(lang).day()];
  if (shorthand) {
    day = moment(date, 'YYYY-MM-DD').locale(lang).format('ddd');
  }
  return day;
};

export {
  URLToObject,
  checkIfRunningInContainer,
  noOverwriteCustomizer,
  createQueryString,
  getFormattedFqtvPrograms,
  getMessageTypes,
  getCurrentPage,
  getCurrentPageAlertName,
  getStateOfCountry,
  objToArrSelectBoxCollection,
  buildHometagsSelectbox,
  areWeInTestMode,
  getInvalidFieldErrorResponse,
  getOperatingSystem,
  parseURL,
  openInNewTab,
  trueDetectDesktop,
  calculateDevice,
  clearQueryDataFromURL,
  scrollToElement,
  isElementInViewport,
  scrollToSeat,
  getMappedMatchboxLocalization,
  createComplexMessageObject,
  getPaxInitials,
  getOperatingAirlineFromCarrierCode,
  toProperCase,
  focusAndScrollIntoView,
  focusFirstInvalidInput,
  getDayOfWeekFromDate,
};
